/**
 * Slider Component Logic
 * Includes both Custom Element fallback and Alpine.js component
 */
console.log('Slider JS loaded');


if (!customElements.get('slider-component')) {
    class SliderComponent extends HTMLElement {
        constructor() {
            super();
            this.slider = this.querySelector('[data-slider]');
            this.prevBtn = this.querySelector('[data-prev], [data-action="prev"], .slider-btn-prev');
            this.nextBtn = this.querySelector('[data-next], [data-action="next"], .slider-btn-next');
            this.pagination = this.querySelector('[data-pagination], [data-slider-controls], .slider-pagination');
            this.slides = this.querySelectorAll('.slider-item, .slider-slide');
            this.active = 0;
            this.total = this.slides.length;
            this.speed = parseInt(this.dataset.speed) || 5000;
            this.autoplay = this.dataset.autoplay === 'true';
            this.timer = null;
            this.isManualScroll = false;
            this.touchStartX = 0;
            this.touchStartY = 0;

            if (this.total > 1) {
                this.init();
            }
        }

        init() {
            if (this.prevBtn) this.prevBtn.addEventListener('click', (e) => { e.preventDefault(); this.prev(); });
            if (this.nextBtn) this.nextBtn.addEventListener('click', (e) => { e.preventDefault(); this.next(); });

            if (this.pagination) {
                const dots = this.pagination.querySelectorAll('[data-pagination-dot], .pagination-dot, .slider-dot');
                dots.forEach((dot, index) => {
                    dot.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.active = index;
                        this.update();
                    });
                });
            }

            if (this.slider) {
                this.slider.addEventListener('scroll', () => {
                    if (this.isManualScroll) return;
                    const slideWidth = this.slides[0]?.offsetWidth || 1;
                    const newActive = Math.round(this.slider.scrollLeft / slideWidth);
                    if (newActive !== this.active && newActive >= 0 && newActive < this.total) {
                        this.active = newActive;
                        this.updateDots();
                    }
                }, { passive: true });
            }

            this.addEventListener('touchstart', (e) => {
                if (!e.touches || !e.touches[0]) return;
                this.touchStartX = e.touches[0].clientX;
                this.touchStartY = e.touches[0].clientY;
            }, { passive: true });

            this.addEventListener('touchend', (e) => {
                if (!e.changedTouches || !e.changedTouches[0]) return;
                const deltaX = e.changedTouches[0].clientX - this.touchStartX;
                const deltaY = e.changedTouches[0].clientY - this.touchStartY;

                if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40) {
                    if (deltaX < 0) {
                        this.next();
                    } else {
                        this.prev();
                    }
                }
            }, { passive: true });

            if (this.autoplay) this.startAutoplay();

            this.addEventListener('mouseenter', () => this.stopAutoplay());
            this.addEventListener('mouseleave', () => this.startAutoplay());

            this.update();
        }

        startAutoplay() {
            this.stopAutoplay();
            this.timer = setInterval(() => this.next(), this.speed);
        }

        stopAutoplay() {
            if (this.timer) clearInterval(this.timer);
        }

        next() {
            this.active = (this.active + 1) % this.total;
            this.update();
        }

        prev() {
            this.active = (this.active - 1 + this.total) % this.total;
            this.update();
        }

        updateDots() {
            if (this.pagination) {
                const dots = this.pagination.querySelectorAll('[data-pagination-dot], .pagination-dot, .slider-dot');
                const activeClass = this.dataset.dotActiveClass || 'is-active';
                const inactiveClass = this.dataset.dotInactiveClass || '';

                dots.forEach((dot, index) => {
                    const isActive = index === this.active;
                    dot.classList.toggle(activeClass, isActive);
                    if (inactiveClass) {
                        dot.classList.toggle(inactiveClass, !isActive);
                    }
                });
            }
        }

        update() {
            this.slides.forEach((slide, index) => {
                const isActive = index === this.active;
                slide.classList.toggle('is-active', isActive);
                if (this.dataset.slideClass) {
                    slide.classList.toggle(this.dataset.slideClass, isActive);
                }
            });

            if (this.slider && this.slider.scrollWidth > this.slider.clientWidth) {
                const activeSlide = this.slides[this.active];
                if (activeSlide) {
                    this.isManualScroll = true;
                    this.slider.scrollTo({
                        left: activeSlide.offsetLeft,
                        behavior: 'smooth'
                    });
                    setTimeout(() => {
                        this.isManualScroll = false;
                    }, 400);
                }
            }

            this.updateDots();
        }
    }
    customElements.define('slider-component', SliderComponent);
}

/**
 * Alpine.js Hero Slider Data
 */
window.heroSliderComponent = (config = {}) => ({
    active: 0,
    total: config.total || 0,
    speed: config.speed || 5000,
    autoplay: config.autoplay || false,
    timer: null,
    isCloned: false,
    isJumping: false,
    touchStartX: 0,
    touchStartY: 0,

    init() {
        if (this.autoplay && this.total > 1) {
            this.startAutoplay();
        }
        if (this.$el) {
            this.$el.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
            this.$el.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
        }
    },

    handleTouchStart(e) {
        if (!e.touches || !e.touches[0]) return;
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
    },

    handleTouchEnd(e) {
        if (!e.changedTouches || !e.changedTouches[0]) return;
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const deltaX = touchEndX - this.touchStartX;
        const deltaY = touchEndY - this.touchStartY;

        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40) {
            if (deltaX < 0) {
                this.next();
            } else {
                this.prev();
            }
        }
    },

    startAutoplay() {
        this.stopAutoplay();
        this.timer = setInterval(() => {
            this.next();
        }, this.speed);
    },

    stopAutoplay() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    },

    next() {
        if (this.isCloned) {
            if (this.active >= this.total) return;
            this.active++;
            if (this.active === this.total) {
                setTimeout(() => {
                    this.isJumping = true;
                    this.active = 0;
                    setTimeout(() => this.isJumping = false, 50);
                }, 500);
            }
        } else {
            this.active = (this.active + 1) % this.total;
        }
        if (this.autoplay) this.startAutoplay();
    },

    prev() {
        if (this.isCloned) {
            if (this.active <= -1) return;
            this.active--;
            if (this.active === -1) {
                setTimeout(() => {
                    this.isJumping = true;
                    this.active = this.total - 1;
                    setTimeout(() => this.isJumping = false, 50);
                }, 500);
            }
        } else {
            this.active = (this.active - 1 + this.total) % this.total;
        }
        if (this.autoplay) this.startAutoplay();
    },

    goTo(index) {
        this.active = index;
        if (this.autoplay) this.startAutoplay();
    }
});

// Register with Alpine
document.addEventListener('alpine:init', () => {
    if (window.Alpine) {
        Alpine.data('heroSliderComponent', window.heroSliderComponent);
    }
});

// Immediate registration if Alpine is already loaded
if (window.Alpine) {
    Alpine.data('heroSliderComponent', window.heroSliderComponent);
}
